import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader, TableSkeleton } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import {
  formatCurrency,
  formatDate,
  getStatusBadgeClass,
  PROPERTY_TYPES,
  PROPERTY_STATUSES,
  SALE_TYPES,
} from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Search, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertiesPage() {
  const { isMD } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    location: '',
    status: 'Available',
    sale_type: '',
    listed_price: '',
    size: '',
    description: '',
    total_units: '1',
    available_units: '1',
    unit_type: 'unit',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    sale_type: '',
    search: '',
  });

  useEffect(() => {
    fetchProperties();
  }, [filters]);

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.sale_type) params.append('sale_type', filters.sale_type);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/properties?${params}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      location: '',
      status: 'Available',
      sale_type: '',
      listed_price: '',
      acquisition_cost: '',
      other_costs: '',
      size: '',
      description: '',
      total_units: '1',
      available_units: '1',
      unit_type: 'unit',
    });
    setFormErrors({});
    setSelectedProperty(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      type: property.type,
      location: property.location,
      status: property.status,
      sale_type: property.sale_type,
      listed_price: property.listed_price,
      acquisition_cost: property.acquisition_cost || '',
      other_costs: property.other_costs || '',
      size: property.size,
      description: property.description || '',
      total_units: property.total_units || 1,
      available_units: property.available_units || 1,
      unit_type: property.unit_type || 'unit',
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const openDeleteDialog = (property) => {
    setSelectedProperty(property);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.type) errors.type = 'Type is required';
    if (!formData.location.trim()) errors.location = 'Location is required';
    if (!formData.sale_type) errors.sale_type = 'Sale type is required';
    if (!formData.listed_price || formData.listed_price <= 0) errors.listed_price = 'Valid price is required';
    if (!formData.size.trim()) errors.size = 'Size is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        listed_price: parseFloat(formData.listed_price),
      };

      if (selectedProperty) {
        await axios.put(`${API}/properties/${selectedProperty.id}`, payload);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, payload);
        toast.success('Property created successfully');
      }

      setModalOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/properties/${selectedProperty.id}`);
      toast.success('Property deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="properties-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl text-lux-text mb-2">Properties</h1>
            <p className="text-lux-muted">Manage your property listings</p>
          </div>
          {isMD && (
            <Button onClick={openAddModal} className="lux-btn-primary" data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="lux-card p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-lux-muted" />
              <Input
                placeholder="Search properties..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="lux-input pl-10"
                data-testid="property-search-input"
              />
            </div>
            <Select
              value={filters.type || "all"}
              onValueChange={(value) => setFilters({ ...filters, type: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="property-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {PROPERTY_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="property-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {PROPERTY_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.sale_type || "all"}
              onValueChange={(value) => setFilters({ ...filters, sale_type: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="property-sale-type-filter">
                <SelectValue placeholder="All Sale Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sale Types</SelectItem>
                {SALE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="lux-card overflow-hidden">
          {loading ? (
            <TableSkeleton rows={5} cols={7} />
          ) : properties.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-16 h-16 text-lux-border mb-4" />
              <p className="text-lux-muted text-lg">No properties found</p>
              {isMD && (
                <Button onClick={openAddModal} className="lux-btn-secondary mt-4">
                  Add your first property
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="lux-table-header">
                  <tr>
                    <th className="px-6 py-4 text-left">Code</th>
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Type</th>
                    <th className="px-6 py-4 text-left">Location</th>
                    <th className="px-6 py-4 text-center">Available</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-right">Price (₦)</th>
                    <th className="px-6 py-4 text-left">Sale Type</th>
                    {isMD && <th className="px-6 py-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {properties.map((property) => (
                    <tr key={property.id} className="lux-table-row" data-testid={`property-row-${property.id}`}>
                      <td className="px-6 py-4 text-lux-gold font-mono text-sm">
                        {property.property_code}
                      </td>
                      <td className="px-6 py-4 text-lux-text font-medium">
                        {property.name}
                      </td>
                      <td className="px-6 py-4 text-lux-muted">{property.type}</td>
                      <td className="px-6 py-4 text-lux-muted">{property.location}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          property.available_units > 0 
                            ? 'bg-green-900/30 text-green-400 border border-green-900/50' 
                            : 'bg-red-900/30 text-red-400 border border-red-900/50'
                        }`}>
                          {property.available_units || 0}/{property.total_units || 1} {property.unit_type || 'unit'}(s)
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(property.status)}`}>
                          {property.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-lux-gold font-semibold">
                        {formatCurrency(property.listed_price)}
                      </td>
                      <td className="px-6 py-4 text-lux-muted text-sm">
                        {property.sale_type}
                      </td>
                      {isMD && (
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(property)}
                              className="text-lux-muted hover:text-lux-gold"
                              data-testid={`edit-property-${property.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(property)}
                              className="text-lux-muted hover:text-red-400"
                              data-testid={`delete-property-${property.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                {selectedProperty ? 'Edit Property' : 'Add New Property'}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-lux-text">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`lux-input ${formErrors.name ? 'border-red-500' : ''}`}
                  placeholder="Property name"
                  data-testid="property-name-input"
                />
                {formErrors.name && <p className="text-red-400 text-xs">{formErrors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className={`lux-input ${formErrors.type ? 'border-red-500' : ''}`} data-testid="property-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.type && <p className="text-red-400 text-xs">{formErrors.type}</p>}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-lux-text">Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={`lux-input ${formErrors.location ? 'border-red-500' : ''}`}
                  placeholder="Property location"
                  data-testid="property-location-input"
                />
                {formErrors.location && <p className="text-red-400 text-xs">{formErrors.location}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="lux-input" data-testid="property-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Sale Type *</Label>
                <Select
                  value={formData.sale_type}
                  onValueChange={(value) => setFormData({ ...formData, sale_type: value })}
                >
                  <SelectTrigger className={`lux-input ${formErrors.sale_type ? 'border-red-500' : ''}`} data-testid="property-sale-type-select">
                    <SelectValue placeholder="Select sale type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SALE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.sale_type && <p className="text-red-400 text-xs">{formErrors.sale_type}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Listed Price (₦) *</Label>
                <Input
                  type="number"
                  value={formData.listed_price}
                  onChange={(e) => setFormData({ ...formData, listed_price: e.target.value })}
                  className={`lux-input ${formErrors.listed_price ? 'border-red-500' : ''}`}
                  placeholder="0"
                  data-testid="property-price-input"
                />
                {formErrors.listed_price && <p className="text-red-400 text-xs">{formErrors.listed_price}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Acquisition Cost (₦)</Label>
                <Input
                  type="number"
                  value={formData.acquisition_cost}
                  onChange={(e) => setFormData({ ...formData, acquisition_cost: e.target.value })}
                  className="lux-input"
                  placeholder="Cost to acquire/develop"
                  data-testid="property-acquisition-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Other Costs (₦)</Label>
                <Input
                  type="number"
                  value={formData.other_costs}
                  onChange={(e) => setFormData({ ...formData, other_costs: e.target.value })}
                  className="lux-input"
                  placeholder="Marketing, legal, etc."
                  data-testid="property-other-costs-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-lux-text">Size *</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className={`lux-input ${formErrors.size ? 'border-red-500' : ''}`}
                  placeholder="e.g. 500sqm"
                  data-testid="property-size-input"
                />
                {formErrors.size && <p className="text-red-400 text-xs">{formErrors.size}</p>}
              </div>

              {/* Units Section */}
              <div className="sm:col-span-2 p-4 bg-blue-900/10 border border-blue-900/30 rounded-lg">
                <h4 className="text-blue-400 text-sm font-medium mb-3">Unit Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-lux-text">Unit Type</Label>
                    <Select
                      value={formData.unit_type}
                      onValueChange={(value) => setFormData({ ...formData, unit_type: value })}
                    >
                      <SelectTrigger className="lux-input" data-testid="property-unit-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unit">Unit</SelectItem>
                        <SelectItem value="flat">Flat</SelectItem>
                        <SelectItem value="plot">Plot</SelectItem>
                        <SelectItem value="acre">Acre</SelectItem>
                        <SelectItem value="sqm">Sqm</SelectItem>
                        <SelectItem value="hectare">Hectare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-lux-text">Total Units</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.total_units}
                      onChange={(e) => {
                        const value = e.target.value;
                        const total = value === '' ? '' : parseInt(value);
                        setFormData({ 
                          ...formData, 
                          total_units: value === '' ? '' : total,
                          available_units: selectedProperty ? formData.available_units : (total || 1)
                        });
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 1) {
                          setFormData({ 
                            ...formData, 
                            total_units: 1,
                            available_units: selectedProperty ? formData.available_units : 1
                          });
                        }
                      }}
                      className="lux-input"
                      data-testid="property-total-units-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-lux-text">Available Units</Label>
                    <Input
                      type="number"
                      min="0"
                      max={formData.total_units}
                      value={formData.available_units}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, available_units: value === '' ? '' : parseInt(value) });
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        const maxUnits = parseInt(formData.total_units) || 1;
                        if (isNaN(value) || value < 0) {
                          setFormData({ ...formData, available_units: 0 });
                        } else if (value > maxUnits) {
                          setFormData({ ...formData, available_units: maxUnits });
                        }
                      }}
                      className="lux-input"
                      data-testid="property-available-units-input"
                    />
                  </div>
                </div>
                <p className="text-lux-muted text-xs mt-2">
                  Price per {formData.unit_type}: {formatCurrency(parseFloat(formData.listed_price) || 0)}
                </p>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label className="text-lux-text">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="lux-input min-h-[100px]"
                  placeholder="Property description..."
                  data-testid="property-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="lux-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="lux-btn-primary"
                data-testid="save-property-btn"
              >
                {saving ? 'Saving...' : selectedProperty ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">Delete Property</AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                Are you sure you want to delete "{selectedProperty?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
